const DEFAULT_FACTOR = 0.05;
const DEFAULT_BREAKPOINT = 778;
const INTERSECTION_ROOT_MARGIN = '20% 0px';

function attrBool(el, name) {
  if (!el.hasAttribute(name)) return false;
  const v = el.getAttribute(name);
  return v !== 'false' && v !== '0';
}

class ParallaxContainer extends HTMLElement {
  static get observedAttributes() {
    return ['factor', 'breakpoint', 'mobile-fill', 'desktop-fill', 'fill'];
  }

  constructor() {
    super();
    this.factor = DEFAULT_FACTOR;
    this.sectionTopDoc = 0;
    this.lastY = 0;
    this.rafId = null;
    this.isInView = false;
    this.section = null;
    this.inner = null;
    this.resizeObserver = null;
    this.documentResizeObserver = null;
    this.intersectionObserver = null;
  }

  connectedCallback() {
    this.readFactor();
    this.section = this.parentElement;
    if (!this.section) return;

    this.style.setProperty('--parallax-scale', String(1 + this.factor));
    this.createInner();
    this.applyLayoutStyles();
    this.cacheSectionTop();
    this.setInnerTransform(this.lastY, true);

    this.setupIntersectionObserver();
    this.setupResizeObserver();
    this.setupDocumentResizeObserver();
    this.attachScrollListeners();
  }

  disconnectedCallback() {
    this.stopRafLoop();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.documentResizeObserver?.disconnect();
  }

  attributeChangedCallback(name) {
    if (name === 'factor') {
      this.readFactor();
      this.style.setProperty('--parallax-scale', String(1 + this.factor));
      return;
    }
    if (
      name === 'breakpoint' ||
      name === 'mobile-fill' ||
      name === 'desktop-fill' ||
      name === 'fill'
    ) {
      this.applyLayoutStyles();
      this.cacheSectionTop();
      if (!this.isInView && this.section) {
        const rectTop = this.sectionTopDoc - this.getScroll();
        this.lastY = -rectTop * this.factor;
        this.setInnerTransform(this.lastY, false);
      }
    }
  }

  readFactor() {
    const value = parseFloat(this.getAttribute('factor'));
    this.factor = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : DEFAULT_FACTOR;
  }

  readBreakpoint() {
    const raw = parseInt(this.getAttribute('breakpoint'), 10);
    if (!Number.isFinite(raw) || raw < 320 || raw > 3840) return DEFAULT_BREAKPOINT;
    return raw;
  }

  isFillActive() {
    const legacy = attrBool(this, 'fill');
    const mobileFill = attrBool(this, 'mobile-fill') || legacy;
    const desktopFill = attrBool(this, 'desktop-fill') || legacy;
    const bp = this.readBreakpoint();
    return window.innerWidth >= bp ? desktopFill : mobileFill;
  }

  createInner() {
    this.style.overflow = 'hidden';
    this.inner = document.createElement('div');
    this.inner.className = 'parallax-container__inner';
    while (this.firstChild) this.inner.appendChild(this.firstChild);
    this.appendChild(this.inner);
  }

  applyLayoutStyles() {
    if (!this.inner) return;
    if (this.isFillActive()) {
      this.style.position = 'absolute';
      this.style.inset = '0';
      this.style.height = '';
      this.style.display = '';
      this.inner.style.position = 'absolute';
      this.inner.style.inset = '0';
      this.inner.style.height = '';
    } else {
      this.style.position = 'relative';
      this.style.display = 'block';
      this.style.height = '100%';
      this.style.inset = '';
      this.inner.style.position = '';
      this.inner.style.inset = '';
      this.inner.style.height = '100%';
    }
  }

  setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const wasInView = this.isInView;
          this.isInView = entry.isIntersecting;
          if (this.isInView) {
            this.startRafLoop();
          } else {
            this.stopRafLoop();
            if (wasInView) this.setInnerTransform(this.lastY, false);
          }
        }
      },
      { rootMargin: INTERSECTION_ROOT_MARGIN, threshold: 0 }
    );
    this.intersectionObserver.observe(this.section);
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => this.recalcOnResize());
    this.resizeObserver.observe(this.section);
  }

  setupDocumentResizeObserver() {
    this.documentResizeObserver = new ResizeObserver(() => this.recalcOnResize());
    this.documentResizeObserver.observe(document.documentElement);
  }

  recalcOnResize() {
    this.applyLayoutStyles();
    this.cacheSectionTop();
    if (this.isInView) return;
    const rectTop = this.sectionTopDoc - this.getScroll();
    this.lastY = -rectTop * this.factor;
    this.setInnerTransform(this.lastY, false);
  }

  attachScrollListeners() {
    document.addEventListener('DOMContentLoaded', () => {
      this.cacheSectionTop();
      if (this.isInView) this.startRafLoop();
    });
  }

  startRafLoop() {
    if (this.rafId != null) return;
    this.tick();
  }

  stopRafLoop() {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  tick = () => {
    if (!this.isInView) {
      this.rafId = null;
      return;
    }
    this.updateParallax();
    this.rafId = requestAnimationFrame(this.tick);
  };

  getScroll() {
    return window.lenis ? window.lenis.scroll : window.scrollY || window.pageYOffset;
  }

  cacheSectionTop() {
    if (this.section) {
      this.sectionTopDoc = this.section.getBoundingClientRect().top + this.getScroll();
    }
  }

  setInnerTransform(y, use3d) {
    if (!this.inner) return;
    const translate = use3d ? `translate3d(0, ${y}px, 0)` : `translateY(${y}px)`;
    this.inner.style.transform = `${translate} scale(var(--parallax-scale, 1.05))`;
  }

  updateParallax() {
    if (!this.section || !this.inner) return;
    const rectTop = this.sectionTopDoc - this.getScroll();
    this.lastY = -rectTop * this.factor;
    this.setInnerTransform(this.lastY, true);
  }
}

customElements.define('parallax-container', ParallaxContainer);
